import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { GetOfferPublishedParticipants_getOfferPublishedParticipants } from "_graphql/visal/queries/__generated__/GetOfferPublishedParticipants";
import { Offer_findSingleOffer } from "_graphql/visal/queries/__generated__/Offer";
import {
  ActionButton,
  RealTimeWrapper,
  Show,
  TableComponent,
} from "components";
import config from "config";
import { useOfferPublishedParticipants } from "hooks/data/use-publish-offers";
import { useUpdateKavaThreadBuble } from "hooks/data/use-update-bubble";
import { Fragment, useState } from "react";
import { useUrlState, withPermissions } from "utils";
import AcceptOfferContainer from "../accept-offer";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";
import OfferTransactionThreadContainer from "../components/offer-transaction-thread";

type Props = {
  isOfferPublished: boolean;
  offer: Offer_findSingleOffer | undefined;
};

const ReinsurerInterests = ({ isOfferPublished, offer }: Props) => {
  const [selectedInterest, setSelectedInterest] = useState(null);
  const [modal, setModal] = useUrlState("modal");
  const navigate = useAppNavigate();
  const searchParams = useAppSearch();

  const { updateBubble } = useUpdateKavaThreadBuble();
  const {
    participants: publishedParticipants,
    loading: participantsLoading,
    refetch: refetchParticipants,
  } = useOfferPublishedParticipants({
    offerId: offer?.offer_id || "",
  });
  const handleAcceptOffer = (
    row: GetOfferPublishedParticipants_getOfferPublishedParticipants
  ) => {
    setSelectedInterest(row as any);
    setModal("acceptOffer");
  };

  const updateThreadBubble = (messageIds: string[]) => {
    if (!messageIds || !messageIds.length) return;

    updateBubble({
      variables: {
        input: messageIds,
      },
    })
      .then(() => {})
      .catch(() => {});
  };

  return (
    <Fragment>
      <Show if={isOfferPublished}>
        <RealTimeWrapper
          channel={config.pusher.channels.kava}
          events={[
            "reinsurer.interest",
            "reinsurer.remove_interest",
            "reinsurer.thread_notification",
            "broker.remove_reinsurer_interest",
            "broker.accepted_interest",
            "broker.thread_notification",
            "broker.published_offer",
          ]}
          callBack={refetchParticipants}
        >
          <div className="bg-card-700 shadow rounded-lg">
            <Disclosure as="div" defaultOpen={true}>
              {({ open }) => (
                <>
                  <Disclosure.Button
                    as="div"
                    className="flex justify-between items-center cursor-pointer w-full px-4 py-3"
                  >
                    <div>
                      <h3 className="text-md leading-6 font-normal text-gray-900 dark:text-gray-100">
                        Reinsurers Interest
                      </h3>
                      <p className="mt-1 text-xs text-gray-500">
                        List of reinsurers who have expressed interest in the
                        offer
                      </p>
                    </div>
                    <div>
                      {open ? (
                        <ChevronUpIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                      ) : (
                        <ChevronDownIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                      )}
                    </div>
                  </Disclosure.Button>
                  <Disclosure.Panel
                    as="div"
                    className="px-4 pb-4 pt-2 border-t border-gray-200 dark:border-gray-700"
                  >
                    <TableComponent
                      title={"interest"}
                      hasSearch
                      loading={participantsLoading}
                      columns={[
                        {
                          accessor: "reinsurer.re_company_name",
                          name: "Company Name",
                        },
                        {
                          accessor: "reinsurer.re_company_email",
                          name: "Company Email",
                        },
                        {
                          accessor: "offer_participant_percentage",
                          name: "Participation %",
                        },
                        {
                          accessor: "reinsurer_representative.rep_first_name",
                          name: "Added By",
                          render: (row) => (
                            <span>
                              {row.reinsurer_representative?.rep_first_name ||
                                "N/A"}{" "}
                            </span>
                          ),
                        },
                        {
                          accessor: "status",
                          name: "Status",
                          render: (row) => (
                            <span
                              className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                row.participation_status === "ACCEPTED"
                                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                                  : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100"
                              }`}
                            >
                              {row.participation_status}
                            </span>
                          ),
                        },
                        {
                          accessor: "",
                          name: "Actions",
                          render: (row) => (
                            <div className="flex space-x-1">
                              <Show
                                if={
                                  offer?.offer_from === "INTERNAL" ||
                                  (offer?.offer_from !== "INTERNAL" &&
                                    offer?.version === "V1")
                                }
                              >
                                {withPermissions([
                                  "*:*",
                                  "offers:*",
                                  "offers:manage",
                                  "offers:update",
                                ])(
                                  <ActionButton
                                    action={"accept"}
                                    tooltip={
                                      Number(
                                        row?.offer_participant_percentage
                                      ) <= 0
                                        ? "You Cannot accept zero percentage"
                                        : "Accept Participation"
                                    }
                                    onClick={() => handleAcceptOffer(row)}
                                    disabled={Boolean(
                                      row.participation_status === "ACCEPTED" ||
                                        Number(
                                          row?.offer_participant_percentage
                                        ) <= 0
                                    )}
                                  />
                                )}
                              </Show>
                              <ActionButton
                                action={"chat"}
                                count={row.unread_messages?.length || 0}
                                tooltip={"Transaction Thread"}
                                callBack={() => {
                                  updateThreadBubble(
                                    row?.unread_messages?.map(
                                      (message) => message?.id || ""
                                    ) || []
                                  );
                                }}
                                onClick={() => {
                                  navigate({
                                    search: (old) => ({
                                      ...old,
                                      id: row.offer_published_participant_id,
                                      modal: "chat",
                                    }),
                                  });
                                }}
                              />
                            </div>
                          ),
                        },
                      ]}
                      data={publishedParticipants}
                      refetch={refetchParticipants}
                    />
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
        </RealTimeWrapper>
      </Show>

      <AcceptOfferContainer
        open={modal === "acceptOffer"}
        setOpen={(val: boolean) => setModal(val ? "acceptOffer" : undefined)}
        reinsurerData={selectedInterest}
      />

      {searchParams?.id && modal === "chat" && (
        <OfferTransactionThreadContainer
          open={modal === "chat"}
          setOpen={(val: boolean) =>
            navigate({
              search(prev) {
                return {
                  ...prev,
                  modal: val ? "chat" : undefined,
                  id: val ? prev?.id : undefined,
                };
              },
            })
          }
          refetch={refetchParticipants}
          // hideInput={offer?.offer_from !== "INTERNAL"}
        />
      )}
    </Fragment>
  );
};

export default ReinsurerInterests;
